import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Spinner, Alert, Pagination } from 'react-bootstrap'
import { api } from '../../services/api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

interface Article {
  id: string
  title: string
  slug: string
  excerpt: string
  isPublished: boolean
  views: number
  helpful: number
  notHelpful: number
  createdAt: string
  updatedAt: string
  category: {
    id: string
    name: string
  } | null
  author: {
    id: string
    name: string
  }
}

interface Category {
  id: string
  name: string
  slug: string
  description: string
  icon?: string
  _count: {
    articles: number
  }
}

export const ArticlesPage = () => {
  const navigate = useNavigate()
  const [articles, setArticles] = useState<Article[]>([])
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryData, setCategoryData] = useState({
    name: '',
    description: '',
    icon: 'fas fa-folder',
    order: 0
  })
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    fetchArticles()
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchArticles()
  }, [page])

  const fetchArticles = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('pageSize', itemsPerPage.toString())
      const response = await api.get(`/admin/articles?${params.toString()}`)
      setArticles(response.data.data || [])
      setTotal(response.data.meta?.total || response.data.data?.length || 0)
    } catch (error) {
      toast.error('Failed to load articles')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/articles/categories/list')
      setCategories(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const handleDeleteArticle = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return

    try {
      await api.delete(`/admin/articles/${id}`)
      toast.success('Article deleted successfully')
      fetchArticles()
    } catch (error) {
      toast.error('Failed to delete article')
    }
  }

  const handleTogglePublish = async (article: Article) => {
    try {
      await api.patch(`/admin/articles/${article.id}`, {
        isPublished: !article.isPublished
      })
      toast.success(`Article ${!article.isPublished ? 'published' : 'unpublished'} successfully`)
      fetchArticles()
    } catch (error) {
      toast.error('Failed to update article')
    }
  }

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (editingCategory) {
        await api.patch(`/admin/articles/categories/${editingCategory.id}`, categoryData)
        toast.success('Category updated successfully')
      } else {
        await api.post('/admin/articles/categories', categoryData)
        toast.success('Category created successfully')
      }
      setShowCategoryModal(false)
      fetchCategories()
      resetCategoryForm()
    } catch (error: any) {
      setError(error.response?.data?.error?.message || 'Failed to save category')
    }
  }

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return

    try {
      await api.delete(`/admin/articles/categories/${id}`)
      toast.success('Category deleted successfully')
      fetchCategories()
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to delete category')
    }
  }

  const openCategoryModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category)
      setCategoryData({
        name: category.name,
        description: category.description || '',
        icon: category.icon || 'fas fa-folder',
        order: 0
      })
    }
    setShowCategoryModal(true)
  }

  const resetCategoryForm = () => {
    setEditingCategory(null)
    setCategoryData({
      name: '',
      description: '',
      icon: 'fas fa-folder',
      order: 0
    })
    setError('')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="fas fa-book me-2 text-primary"></i>
          Articles Management
        </h2>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => openCategoryModal()}>
            <i className="fas fa-folder-plus me-2"></i>
            New Category
          </Button>
          <Button variant="primary" onClick={() => navigate('/admin/articles/new')}>
            <i className="fas fa-plus me-2"></i>
            New Article
          </Button>
        </div>
      </div>

      {/* Categories Section */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-folder me-2 text-primary"></i>
                  Article Categories
                </h5>
                <Button variant="primary" size="sm" onClick={() => openCategoryModal()}>
                  <i className="fas fa-plus me-2"></i>
                  Add Category
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-3">
                {categories.length === 0 ? (
                  <div className="text-center w-100 py-4">
                    <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                    <p className="text-muted mb-2">No categories yet.</p>
                    <p className="text-muted small">Create categories to organize your knowledge base articles.</p>
                    <Button variant="primary" size="sm" onClick={() => openCategoryModal()}>
                      <i className="fas fa-plus me-2"></i>
                      Create First Category
                    </Button>
                  </div>
                ) : (
                  categories.map((category) => (
                    <Card key={category.id} className="border" style={{ width: '200px' }}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            {category.icon && <i className={`${category.icon} fa-2x text-primary`}></i>}
                          </div>
                          <div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              onClick={() => openCategoryModal(category)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </div>
                        <h6 className="mb-1">{category.name}</h6>
                        <small className="text-muted">{category._count.articles} articles</small>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Articles Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">All Articles ({articles.length})</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-book fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No articles yet</h5>
              <p className="text-muted">Create your first article to get started</p>
              <Button variant="primary" onClick={() => navigate('/admin/articles/new')}>
                <i className="fas fa-plus me-2"></i>
                Create Article
              </Button>
            </div>
          ) : (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Helpful</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td>
                      <div>
                        <strong>{article.title}</strong>
                        <br />
                        <small className="text-muted">{article.slug}</small>
                      </div>
                    </td>
                    <td>
                      {article.category ? (
                        <Badge bg="primary">{article.category.name}</Badge>
                      ) : (
                        <span className="text-muted">Uncategorized</span>
                      )}
                    </td>
                    <td>{article.author.name}</td>
                    <td>
                      <Badge bg={article.isPublished ? 'success' : 'warning'}>
                        {article.isPublished ? 'Published' : 'Draft'}
                      </Badge>
                    </td>
                    <td>{article.views}</td>
                    <td>
                      <i className="fas fa-thumbs-up text-success me-1"></i>
                      {article.helpful}
                      <span className="text-muted mx-1">/</span>
                      <i className="fas fa-thumbs-down text-danger me-1"></i>
                      {article.notHelpful}
                    </td>
                    <td>{formatDate(article.updatedAt)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/admin/articles/${article.id}/edit`)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant={article.isPublished ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          onClick={() => handleTogglePublish(article)}
                        >
                          <i className={article.isPublished ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteArticle(article.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {Math.ceil(total / itemsPerPage) > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
            
            {[...Array(Math.ceil(total / itemsPerPage))].map((_, i) => {
              const pageNum = i + 1
              const totalPages = Math.ceil(total / itemsPerPage)
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= page - 1 && pageNum <= page + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === page}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                )
              } else if (pageNum === page - 2 || pageNum === page + 2) {
                return <Pagination.Ellipsis key={pageNum} disabled />
              }
              return null
            })}
            
            <Pagination.Next onClick={() => setPage(page + 1)} disabled={page === Math.ceil(total / itemsPerPage)} />
            <Pagination.Last onClick={() => setPage(Math.ceil(total / itemsPerPage))} disabled={page === Math.ceil(total / itemsPerPage)} />
          </Pagination>
        </div>
      )}

      {/* Category Modal */}
      <Modal show={showCategoryModal} onHide={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCategory ? 'Edit Category' : 'New Category'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSaveCategory}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={categoryData.name}
                onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={categoryData.description}
                onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Icon (Font Awesome class)</Form.Label>
              <Form.Control
                type="text"
                value={categoryData.icon}
                onChange={(e) => setCategoryData({ ...categoryData, icon: e.target.value })}
                placeholder="e.g. fas fa-folder"
              />
              <Form.Text className="text-muted">
                Preview: {categoryData.icon && <i className={`${categoryData.icon} ms-2`}></i>}
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  )
}







